// Asset Group management service - Updated to match Postman Collection
import { getCurrentConfig, getApiScope } from '../config';
import { API_ENDPOINTS, DEFAULT_HEADERS } from '../config/constants';

// Define AssetGroup interface based on Postman collection
export interface AssetGroup {
  id: number;
  assetID: number;
  lanetypeid: number;
  laneassignmentID: number;
  created_at: string;
  updated_at: string;
}

interface CreateAssetGroupData {
  assetID: number;
  lanetypeid: number;
  laneassignmentID: number;
}

interface UpdateAssetGroupData {
  assetID?: number;
  lanetypeid?: number;
  laneassignmentID?: number;
}

class AssetGroupService {
  private getBaseURL(): string {
    return getCurrentConfig().apiBaseUrl;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const baseUrl = this.getBaseURL();
    
    // Fix double slash issue - simple URL construction
    let url = baseUrl + endpoint;
    url = url.replace(/\/+/g, '/'); // Replace multiple slashes with single slash
    url = url.replace(':/', '://'); // Fix protocol if we accidentally broke it
    
    // Get access token if available (same pattern as api.ts)
    let token: string | null = null;
    try {
      const config = getCurrentConfig();
      
      if (config.auth.enableAuthentication) {
        const { msalInstance } = await import('../contexts/AuthContext');
        const accounts = msalInstance.getAllAccounts();
        
        if (accounts.length > 0) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: [getApiScope()], // Use scope from .env
            account: accounts[0]
          });
          token = tokenResponse.accessToken;
        }
      }
    } catch (error) {
      // Silent fail for token acquisition
    }
    
    const headers: Record<string, string> = {
      ...DEFAULT_HEADERS,
    };
    
    // Add any additional headers from options
    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          headers[key] = value;
        }
      });
    }
    
    // Add authorization header if token is available
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      headers,
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Get all asset groups
  async getAllAssetGroups(): Promise<AssetGroup[]> {
    return this.request<AssetGroup[]>(API_ENDPOINTS.ASSET_GROUPS);
  }

  // Get asset group by ID
  async getAssetGroupById(id: number): Promise<AssetGroup> {
    return this.request<AssetGroup>(API_ENDPOINTS.ASSET_GROUP_BY_ID(id));
  }

  // Create new asset group
  async createAssetGroup(data: CreateAssetGroupData): Promise<AssetGroup> {
    return this.request<AssetGroup>(API_ENDPOINTS.ASSET_GROUPS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Update asset group
  async updateAssetGroup(id: number, data: UpdateAssetGroupData): Promise<AssetGroup> {
    return this.request<AssetGroup>(API_ENDPOINTS.ASSET_GROUP_BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Delete asset group
  async deleteAssetGroup(id: number): Promise<{ message: string }> {
    return this.request<{ message: string }>(API_ENDPOINTS.ASSET_GROUP_BY_ID(id), {
      method: 'DELETE',
    });
  }
}

export const assetGroupService = new AssetGroupService();
export default assetGroupService;
